/**
 * Email slide-in behaviour.
 *
 * Markup, gating and settings live in sections/email-slide-in.liquid. This
 * file only decides when to show it and remembers that she said no.
 *
 * Trigger is scroll depth plus a minimum time on page, with desktop
 * exit-intent as a second path. Both are user actions, so the bar never
 * appears on arrival, which is the case Google's intrusive interstitial
 * guidance is actually about.
 *
 * It yields to two things it must never sit on top of: a Klaviyo flyout, if
 * one does manage to render, and the theme's own fixed bottom CTA bars
 * (assets/pdp-sticky-atc.js and sections/tah-sticky-cta.liquid). Rather than
 * hardcode their heights it measures whatever fixed element is currently
 * occupying the bottom edge and sits above it.
 */
(function () {
  var bar = document.querySelector('[data-email-slide-in]');
  if (!bar) return;

  var STORAGE_KEY = 'aa_email_slide_in_dismissed_until';
  var scrollPercent = parseInt(bar.getAttribute('data-scroll-percent'), 10) || 40;
  var minSeconds = parseInt(bar.getAttribute('data-min-seconds'), 10);
  if (isNaN(minSeconds)) minSeconds = 5;
  var dismissDays = parseInt(bar.getAttribute('data-dismiss-days'), 10) || 1;
  // Two different answers, two different memories. Closing it means "not now":
  // ask again after dismissDays (1 by default). Signing up means "done": never
  // ask again. "Never" is as long as the browser will remember. It is kept in
  // localStorage AND a first-party cookie, because either one can be cleared
  // without the other. Chrome caps a cookie at 400 days and Safari trims both
  // stores for visitors who stay away, so a bride who comes back keeps it and
  // one who vanishes for a year may be asked once more. Logged-in subscribers
  // are already skipped in Liquid (customer.accepts_marketing).
  var SUBSCRIBED_KEY = 'aa_email_subscribed';

  var shown = false;
  var closed = false;
  var yielded = false;
  var readyAt = Date.now() + minSeconds * 1000;

  /* localStorage throws in some privacy modes; a capture surface is never
     worth breaking the page over. */
  function readDismissedUntil() {
    try {
      return parseInt(window.localStorage.getItem(STORAGE_KEY), 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  function writeDismissedUntil(ts) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(ts));
    } catch (e) {
      /* nothing to do; she just sees it again next visit */
    }
  }

  function isSubscribed() {
    try {
      if (window.localStorage.getItem(SUBSCRIBED_KEY) === '1') return true;
    } catch (e) {}
    return document.cookie.split('; ').indexOf(SUBSCRIBED_KEY + '=1') !== -1;
  }

  function writeSubscribed() {
    try {
      window.localStorage.setItem(SUBSCRIBED_KEY, '1');
    } catch (e) {}
    document.cookie = SUBSCRIBED_KEY + '=1; path=/; max-age=' + 400 * 24 * 60 * 60 + '; SameSite=Lax';
  }

  // The form posts and the page reloads. If this load is the success state,
  // make sure the flag is down even if the submit handler lost the race.
  if (bar.querySelector('[data-slide-in-success]')) writeSubscribed();

  if (isSubscribed()) return;
  if (readDismissedUntil() > Date.now()) return;

  // A Klaviyo flyout that did render wins. Two email asks on one screen is
  // worse than none.
  function klaviyoFormVisible() {
    var candidates = document.querySelectorAll('.klaviyo-form, [class*="kl-private-reset"], [data-testid="form-row"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.offsetParent !== null && el.getBoundingClientRect().height > 40) return true;
    }
    return false;
  }

  // Measure whatever fixed bar currently owns the bottom edge so we can sit
  // above it instead of on it.
  function bottomObstructionHeight() {
    var bars = document.querySelectorAll('.pdp-sticky-atc, .tah-sticky-cta');
    var viewportH = window.innerHeight || document.documentElement.clientHeight;
    var tallest = 0;
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      if (el === bar || el.hidden) continue;
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;
      var r = el.getBoundingClientRect();
      // Only count it if it is actually pinned to the bottom of the viewport.
      if (r.height > 0 && r.bottom > viewportH - 4 && r.top < viewportH) {
        if (r.height > tallest) tallest = r.height;
      }
    }
    return tallest;
  }

  function positionAboveObstructions() {
    bar.style.bottom = bottomObstructionHeight() + 'px';
  }

  function show() {
    if (shown || closed || yielded) return;
    if (Date.now() < readyAt) return;
    if (klaviyoFormVisible()) return;
    shown = true;
    positionAboveObstructions();
    bar.hidden = false;
    // One frame between unhiding and the transform so the slide actually runs.
    window.requestAnimationFrame(function () {
      bar.classList.add('is-visible');
    });
  }

  /**
   * Stand down if a Klaviyo flyout turns up.
   *
   * Checking once at show time is not enough. Klaviyo's script is held back
   * until first interaction, so its form routinely renders AFTER this bar has
   * already slid up, and observed on the draft it landed straight on top of
   * it: two email asks on one screen, which is worse than either alone. Its
   * flyout carries the 10% offer and richer fields, so it wins.
   *
   * No dismissal is written when yielding. She did not say no to anything, and
   * on a later page where Klaviyo never loads this bar should still be free to
   * appear.
   */
  function yieldToKlaviyo() {
    if (yielded) return;
    if (!klaviyoFormVisible()) return;
    yielded = true;
    bar.classList.remove('is-visible');
    window.setTimeout(function () {
      bar.hidden = true;
    }, 300);
  }

  var klCheckPending = false;
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function () {
      if (klCheckPending) return;
      klCheckPending = true;
      // Klaviyo mutates heavily while its form mounts; coalesce the churn into
      // one geometry read rather than measuring on every batch.
      window.setTimeout(function () {
        klCheckPending = false;
        yieldToKlaviyo();
      }, 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function close() {
    closed = true;
    bar.classList.remove('is-visible');
    writeDismissedUntil(Date.now() + dismissDays * 24 * 60 * 60 * 1000);
    window.setTimeout(function () {
      bar.hidden = true;
    }, 300);
  }

  var closeBtn = bar.querySelector('[data-slide-in-close]');
  if (closeBtn) closeBtn.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && shown && !closed) close();
  });

  // A submit is a signup, not a dismissal. The form posts and the page
  // reloads, so the flag has to be written before she leaves this page.
  var form = bar.querySelector('form');
  if (form) {
    form.addEventListener('submit', writeSubscribed);
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      if (yielded) return;
      if (shown) {
        positionAboveObstructions();
        return;
      }
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = (window.scrollY / scrollable) * 100;
      if (pct >= scrollPercent) show();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  // Desktop exit-intent. Pointer leaving through the top of the window only;
  // there is no mouse on the 83% of sessions that are mobile, which is exactly
  // why exit-intent cannot be the primary trigger here.
  document.addEventListener('mouseout', function (e) {
    if (shown || closed) return;
    if (e.relatedTarget || e.clientY > 0) return;
    show();
  });

  // If she has already scrolled past the threshold when the timer expires
  // (restored scroll position, a jump link), show without waiting for another
  // scroll event.
  if (minSeconds > 0) {
    window.setTimeout(onScroll, minSeconds * 1000 + 50);
  }
})();
